import cx from "classnames";
import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import React, { useEffect, useState } from "react";
import Lazy from "yup/lib/Lazy";
import { getStyles } from "../../../assets/js/utils/functions";
import { getSizedImage } from "../../../assets/js/utils/utils";
import PagesLoaderContext from "../../../contexts/page-loader-context";
import useScreenSize, { BreakPoint } from "../../hooks/useScreenSize";
import LazyImage from "../../lazy-image";
import CircularProgress from "../../ui/circular-progress";
import AnimatedHeaderText from "./animated-header-text";

interface IProps {
  steps: {
    description: string;
    image: string;
    mockupConf?: {
      from?: string;
      to?: string;
    };
  }[];
  title?: React.ReactElement;
  headerTextSeries: string[][];
}

const HowItWorks: React.FC<IProps> = ({ steps, title, headerTextSeries }) => {
  const { animationHasPlayed } = PagesLoaderContext.useContainer();
  const { screen } = useScreenSize();
  const [animProgress, setAnimProgress] = useState(0);
  const stepsCount = steps.length;

  useEffect(() => {
    if (!animationHasPlayed) return;

    const stepsWrapper = document.querySelector(".steps-wrapper");
    const steps = document.querySelectorAll(".steps li");

    gsap.registerPlugin(ScrollTrigger);
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: stepsWrapper,
        pin: true,
        start: "top top",
        pinSpacing: true,
        scrub: true,
        end: "185% top",
        snap: 1 / stepsCount,
        anticipatePin: 0.1,
      },
      onUpdate: () => {
        const progress = tl.progress();

        setAnimProgress(progress);
      },
    });

    steps.forEach((step) => {
      tl.to(step, {
        duration: 3,
      });
    });

    return () => {
      tl.kill();
    };
  }, [animationHasPlayed]);

  const activeIndex = Math.max(Math.floor((animProgress - 0.01) * stepsCount), 0);
  const isActive = (index: number) => animProgress > 0 && Math.floor((animProgress - 0.01) * stepsCount) === index; //0.25 = 1/steps //0.01 is offset for when progress reaches boundaries, since we can't manually check for < the actual progress
  const isPast = (index: number) => index < Math.floor((animProgress - 0.01) * stepsCount);

  return (
    <section className="body-padding section-spacing steps-wrapper">
      <div className="container">
        {/* <h2 className="heading-three font-bold text-black pt-4">{title}</h2> */}
        <AnimatedHeaderText className="mb-10 sm:mb-12.5 lg:mb-15" series={headerTextSeries} />
        <div className="w-full grid md:grid-cols-[47%,53%] lg:grid-cols-[51%,49%] xl:grid-cols-[53%,47%]">
          <div className="min-w-0 md:pr-3 mb-8 md:mb-0 relative">
            <div
              className="progress w-1 bg-page absolute left-0 top-0 z-10 steps-progress"
              style={{ height: `${animProgress * 100}%` }}
            ></div>
            <ul className="flex flex-col items-start md:max-w-[380px] lg:max-w-[410px] xl:max-w-[480px] relative overflow-hidden steps">
              {steps.map((step, index) => (
                <li
                  className={cx(
                    `px-2.5 py-3.75 sm:px-3.75 sm:py-4 xl:px-5 lg:py-4.5 border-l-4 border-grey-fields-100 md:!top-0 md:!relative hiw-step`,
                    {
                      past: isPast(index),
                      active: isActive(index),
                      next: !isPast(index) && !isActive(index),
                    }
                  )}
                  key={index}
                >
                  <figure
                    className={cx(
                      "w-8 h-8 sm:h-10 sm:w-10 rounded-full bg-grey-fields-200 font-display text-sm sm:text-base font-black flex items-center justify-center transition-all ease-out duration-300",
                      { "text-grey-muted": !isActive(index), "text-page": isActive(index) }
                    )}
                  >
                    0{index + 1}
                  </figure>
                  <p
                    className={cx(
                      "mt-3.75 text-1sm sm:text-base md:text-1sm lg:text-base xl:text-lg font-medium !leading-tight transition-all ease-out duration-300",
                      { "text-dark": !isActive(index), "text-primary-900": isActive(index) }
                    )}
                  >
                    {step.description}
                  </p>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-page-pastel min-w-0 rounded-3xl relative overflow-hidden w-full pt-[115%] sm:pt-[85%] md:pt-0">
            <div className="flex items-center absolute top-5 right-5">
              <div className="text-page">
                <CircularProgress progress={animProgress} baseColor="#FFFFFF" width={24} outline={4} />
              </div>
              <span className="text-black font-bold text-sm inline-block ml-1.25 w-6">
                {Math.floor((animProgress - 0.01) * stepsCount) + 1}/{stepsCount}
              </span>
            </div>
            {steps.map((step, index) => (
              <>
                {activeIndex === index && (
                  <figure
                    className="w-[65%] sm:w-[50%] md:w-[68%] lg:w-[65%] xl:w-[57%] absolute left-1/2 transform -translate-x-1/2 top-[12.5%] story-image-appear"
                    style={getStyles({
                      "--from": step?.mockupConf?.from ?? "10%",
                      "--to": step?.mockupConf?.to ?? "0",
                    })}
                  >
                    <LazyImage
                      src={getSizedImage(step.image, { [BreakPoint.xs]: "w_600", default: "w_700" }, screen)}
                      alt={step.description}
                      showLoader={false}
                      className="w-full"
                    />
                  </figure>
                )}
              </>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
